<template>
  <v-navigation-drawer
    :value="isOpenRoleEditor"
    :permanent="isOpenRoleEditor"
    width="500px"
    max-width="100vw"
    right
    fixed
    temporary
    style="z-index: 11;transition: 0.5s cubic-bezier(0.4, 0, 0.2, 1);"
  >
    <v-card min-height="100vh" color="grey lighten-3">
      <BaseToolbar
        @close="close"
        @save="submitData"
        :title="title"
        type-title="icon"
      >
        <v-divider vertical />
        <v-btn
          v-if="['detail'].includes(roleEditorMode)"
          @click="setRoleEditorMode('edit')"
          :loading="loading"
          text
        >
          <v-icon>edit</v-icon>
        </v-btn>
        <v-btn
          v-else
          @click="submitData()"
          :loading="loading"
          text
        >
          <v-icon>save</v-icon>
        </v-btn>
      </BaseToolbar>
      <v-progress-linear
        v-if="loading"
        indeterminate
        height="5"
        class="my-0"
      />
      <v-card-text class="pa-4 overflow-y-auto" style="max-height: calc(100vh - 64px);">
        <v-container class="pa-5">
          <v-row dense>
            <v-col cols="12">
              <v-text-field
                v-model="payload.name"
                v-validate="'required'"
                :label="$t('roles.fields.name')"
                :data-vv-name="$t('roles.fields.name')"
                :error-messages="[...errors.collect($t('roles.fields.name')), ...(errorMessages.name || [])]"
                :readonly="loading || ['detail'].includes(roleEditorMode)"
                @input="errorMessages.name = []"
              ></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-textarea
                v-model="payload.description"
                v-validate="'max:250'"
                :label="$t('roles.fields.description')"
                :data-vv-name="$t('roles.fields.description')"
                :error-messages="[...errors.collect($t('roles.fields.description')), ...(errorMessages.description || [])]"
                :readonly="loading || ['detail'].includes(roleEditorMode)"
                counter="250"
                no-resize
                rows="3"
                @input="errorMessages.description = []"
              ></v-textarea>
            </v-col>
            <v-col cols="12">
              <v-switch
                v-model="payload.is_admin"
                :label="$t('roles.fields.is_admin')"
                :readonly="loading || ['detail'].includes(roleEditorMode)"
              ></v-switch>
            </v-col>
            <v-col cols="12">
              <v-select
                v-model="sectionSelected"
                :items="filterSections"
                item-text="name"
                item-value="id"
                :label="$tc('sections.title', 1)"
                clearable
              ></v-select>
            </v-col>
            <v-col cols="12">
              <v-simple-table>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">
                        {{ $t('roles.fields.permissions') }}
                      </th>
                      <th class="text-venter" style="width: 0;">
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="!filterPermissions.length">
                      <td colspan="2">
                        <div class="text-center pa-12 my-12">
                          <v-img
                            :src="require('@/assets/svg/box_empty.svg')"
                            height="150"
                            max-height="150px"
                            aspect-ratio="1"
                            contain
                          />
                          <div class="grey--text">
                            {{ $t('common.no_results_found') }}
                          </div>
                        </div>
                      </td>
                    </tr>
                    <tr
                      v-for="permission in filterPermissions"
                      :key="permission.id"
                    >
                      <td>{{ permission.name }}</td>
                      <td>
                        <v-checkbox
                          v-model="payload.permissions"
                          :value="permission.id"
                          hide-details
                          class="ma-0 pa-0"
                          :readonly="loading || ['detail'].includes(roleEditorMode)"
                        ></v-checkbox>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
    </v-card>
  </v-navigation-drawer>
</template>

<script>
import BaseToolbar from '@/components/BaseToolbar'
import { mapActions, mapGetters, mapMutations } from 'vuex'

export default {
  name: 'RoleEditor',
  components: {
    BaseToolbar
  },
  data: () => ({
    loading: false,
    errorMessages: {},
    payload: {
      id: null,
      name: '',
      description: '',
      is_admin: false,
      permissions: []
    },
    sectionSelected: null
  }),
  computed: {
    ...mapGetters({
      isOpenRoleEditor: 'roles/isOpenRoleEditor',
      roleEditorMode: 'roles/roleEditorMode',
      role: 'roles/role',
      sections: 'sections/sections',
      permissions: 'permissions/permissions'
    }),
    title () {
      if (this.roleEditorMode === 'create') {
        return 'Crear role'
      }
      if (this.roleEditorMode === 'detail') {
        return 'Detalle de role'
      }
      return 'Editar role'
    },
    filterSections () {
      return this.sections.filter(element => {
        return element.is_admin === this.payload.is_admin
      })
    },
    filterPermissions () {
      return this.permissions.filter(element => {
        return (element.is_admin === this.payload.is_admin) && (element.section_id === this.sectionSelected)
      })
    }
  },
  watch: {
    isOpenRoleEditor (newVal) {
      if (newVal) {
        this.loadDefault()
      }
    },
    role (newVal) {
      if (newVal.id) {
        this.setPayload(newVal)
      }
    },
    'payload.is_admin': function (newVal) {
      this.sectionSelected = null
    }
  },
  created () {
    this.loadDefault()
  },
  destroyed () {
    this.resetSectionState()
  },
  methods: {
    ...mapActions({
      resetSectionState: 'sections/resetState',
      getSections: 'sections/getSections',
      getPermissions: 'permissions/getPermissions',
      saveRole: 'roles/saveRole',
      updateRole: 'roles/updateRole'
    }),
    ...mapMutations({
      toggleRoleEditor: 'roles/toggleRoleEditor',
      setRole: 'roles/setRole',
      setRoleEditorMode: 'roles/setRoleEditorMode'
    }),
    loadDefault () {
      this.getSections({
        fields: 'id,name,is_admin',
        limit: 'all',
        sort: 'id:asc'
      })
      this.getPermissions({
        fields: 'id,action_id,section_id,name,slug,is_admin',
        limit: 'all',
        sort: 'id:asc'
      })
    },
    async submitData () {
      const isValid = await this.$validator.validateAll()
      if (!isValid) { return }
      const {
        payload,
        roleEditorMode,
        saveRole,
        updateRole
      } = this
      this.loading = true
      let result
      if (['create'].includes(roleEditorMode)) {
        result = await saveRole(payload)
      } else {
        result = await updateRole(payload)
      }
      this.loading = false
      if (result.success) {
        this.close()
      } else {
        this.setErrorMessages(result.errors)
      }
    },
    setErrorMessages (errors) {
      errors.forEach(({ field, message, validation }) => {
        if (!this.errorMessages[field]) {
          this.errorMessages[field] = []
        }
        this.errorMessages[field].push(this.$t(`messages.validation_error.${validation}`, null, { field: this.$t(`roles.fields.${field}`) }))
      })
    },
    setPayload (payload) {
      const {
        id = null,
        name = '',
        description = '',
        is_admin = false,
        permissions = []
      } = payload
      this.payload = {
        id,
        name,
        description,
        is_admin,
        permissions: permissions.map(({ id }) => id)
      }
    },
    setDefaultData () {
      this.loading = false
      this.errorMessages = {}
      this.payload = {
        id: null,
        name: '',
        description: '',
        is_admin: false,
        permissions: []
      }
      this.sectionSelected = null
    },
    cleanData () {
      this.setDefaultData()
      this.setRole({})
      this.$validator.reset()
    },
    close () {
      this.toggleRoleEditor()
      this.cleanData()
    }
  }
}
</script>
